import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

const plans = [
  {
    id: "basic",
    name: "Basic",
    price: 29,
    period: "month",
    description: "Get started with your fitness journey",
    features: [
      "Access to workout plans",
      "Access to food plans",
      "Messaging with trainer",
    ],
    disabled: [],
    color: "#3b82f6",
  },
  {
    id: "premium",
    name: "Premium",
    price: 59,
    period: "month",
    description: "Everything you need to level up",
    features: [
      "All Basic features",
      "Progress tracking",
      "Book 1:1 sessions",
      "Training load analytics",
      "Activity import (FIT/GPX/TCX)",
      "Personal records",
    ],
    disabled: [],
    color: "#8b5cf6",
  },
  {
    id: "vip",
    name: "VIP",
    price: 99,
    period: "month",
    description: "The complete experience",
    features: [
      "All Premium features",
      "Priority support",
      "Custom workout plans",
      "Custom meal plans",
      "Weekly check-ins",
    ],
    disabled: [],
    color: "#f59e0b",
  },
];

export function PricingPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentPlan = user?.subscription?.plan;
  const isActive = user?.subscription?.status === "active";

  const subscribe = async (planId: string) => {
    setLoading(planId);
    setError(null);
    try {
      const response = await api.post<{ data: { url: string } }>(
        "/api/stripe/create-checkout-session",
        { plan: planId },
      );
      if (response.data?.url) {
        window.location.assign(response.data.url);
      }
    } catch (_e) {
      setError("Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const manageBilling = async () => {
    setLoading("portal");
    setError(null);
    try {
      const response = await api.post<{ data: { url: string } }>(
        "/api/stripe/create-portal-session",
        {},
      );
      if (response.data?.url) {
        window.open(response.data.url, "_blank");
      }
    } catch (_e) {
      setError("Failed to open billing portal. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div style={{ padding: "20px 16px", maxWidth: 800, margin: "0 auto", background: "#1a1a1a", minHeight: "100vh" }}>
      <h1
        style={{
          fontSize: 24,
          fontWeight: 700,
          textAlign: "center",
          marginBottom: 8,
          color: "#fff",
        }}
      >
        Choose Your Plan
      </h1>
      <p
        style={{
          textAlign: "center",
          color: "#999",
          marginBottom: 32,
          fontSize: 14,
        }}
      >
        Unlock your full potential with Forge
      </p>

      {error && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: 8,
            background: "#3a1a1a",
            border: "1px solid #5c2222",
            color: "#fca5a5",
            fontSize: 14,
            marginBottom: 20,
          }}
        >
          {error}
        </div>
      )}

      {isActive && currentPlan && (
        <div
          style={{
            padding: "16px 20px",
            borderRadius: 12,
            background: "#1a2e1a",
            border: "1px solid #2d5a2d",
            marginBottom: 24,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <p style={{ margin: 0, fontSize: 14, color: "#86efac" }}>
              Current plan: <strong>{currentPlan.toUpperCase()}</strong>
            </p>
            {user?.subscription?.period_end && (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6ee7b7" }}>
                Renews on{" "}
                {new Date(user.subscription.period_end * 1000).toLocaleDateString()}
              </p>
            )}
          </div>
          <button
            onClick={manageBilling}
            disabled={loading === "portal"}
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border: "1px solid #22c55e",
              background: "transparent",
              color: "#22c55e",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {loading === "portal" ? "Loading..." : "Manage Billing"}
          </button>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
        }}
      >
        {plans.map((plan) => {
          const isCurrent = currentPlan === plan.id && isActive;
          const isDowngrade = isActive && currentPlan
            ? ["basic", "premium", "vip"].indexOf(plan.id) <= ["basic", "premium", "vip"].indexOf(currentPlan)
            : false;

          return (
            <div
              key={plan.id}
              style={{
                background: "#2a2a2a",
                borderRadius: 12,
                padding: 24,
                border: isCurrent
                  ? `2px solid ${plan.color}`
                  : "1px solid #3a3a3a",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: plan.color,
                  marginBottom: 12,
                }}
              />
              <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700 }}>
                {plan.name}
              </h3>
              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: 13,
                  color: "#999",
                  minHeight: 36,
                }}
              >
                {plan.description}
              </p>
              <div style={{ marginBottom: 16 }}>
                <span
                  style={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: "#fff",
                  }}
                >
                  ${plan.price}
                </span>
                <span style={{ fontSize: 14, color: "#888" }}>
                  /{plan.period}
                </span>
              </div>

              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: "0 0 20px",
                  flex: 1,
                }}
              >
                {plan.features.map((feature, idx) => (
                  <li
                    key={idx}
                    style={{
                      padding: "6px 0",
                      fontSize: 13,
                      color: "#ccc",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                    }}
                  >
                    <span style={{ color: plan.color, fontWeight: 700 }}>
                      ✓
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <button
                  disabled
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: 6,
                    border: "none",
                    background: "#1a3a1a",
                    color: "#4ade80",
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  Current Plan
                </button>
              ) : (
                <button
                  onClick={() => subscribe(plan.id)}
                  disabled={loading === plan.id || (isDowngrade && false)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: 6,
                    border: "none",
                    background: plan.color,
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: loading === plan.id ? "not-allowed" : "pointer",
                    opacity: loading === plan.id ? 0.7 : 1,
                  }}
                >
                  {loading === plan.id ? "Redirecting..." : "Subscribe"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
